import type { CSSProperties, MouseEvent } from 'react'
import { Link, useLocation } from 'react-router-dom'
import type { StorefrontNavItem } from '@/utils/shopStorefrontNav'

function pathnameOnly(path: string): string {
  const i = path.indexOf('#')
  return i === -1 ? path : path.slice(0, i)
}

function scrollTopIfSamePage(
  e: MouseEvent<HTMLAnchorElement>,
  location: { pathname: string; hash: string },
  targetPath: string
) {
  const path = pathnameOnly(targetPath)
  if (location.pathname !== path) return
  if (location.hash) return
  e.preventDefault()
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

type Props = {
  items: StorefrontNavItem[]
  primaryColor: string
  /** Close the drawer when a destination is chosen */
  onNavigate?: () => void
}

/**
 * Vertical nav for small screens (hamburger panel). Desktop uses {@link ShopStorefrontNav}.
 */
export function ShopStorefrontMobileNav({ items, primaryColor, onNavigate }: Props) {
  const location = useLocation()

  const itemClass = (path: string) => {
    const onPage = location.pathname === pathnameOnly(path)
    return [
      'block rounded-md px-2 py-2.5 text-[17px] font-semibold tracking-tight transition-colors',
      onPage ? '' : 'text-slate-700 active:bg-slate-100',
    ].join(' ')
  }

  const subItemClass = (path: string) => {
    const onPage = location.pathname === pathnameOnly(path)
    return [
      'block rounded-md py-2 pl-3 text-[15px] font-medium transition-colors',
      onPage ? '' : 'text-slate-600 active:bg-slate-100',
    ].join(' ')
  }

  const itemStyle = (path: string): CSSProperties | undefined =>
    location.pathname === pathnameOnly(path) ? { color: primaryColor } : undefined

  const handleNav = () => {
    onNavigate?.()
  }

  return (
    <nav aria-label="Main" className="shop-storefront-mobile-nav flex flex-col">
      <ul className="flex flex-col gap-0.5">
        {items.map((item) => {
          const key = `${item.to}::${item.label}`
          return (
            <li key={key} className="border-b border-slate-100 last:border-b-0">
              <Link
                to={item.to}
                className={itemClass(item.to)}
                style={itemStyle(item.to)}
                onClick={(e) => {
                  scrollTopIfSamePage(e, location, item.to)
                  handleNav()
                }}
              >
                {item.label}
              </Link>
              {item.children && item.children.length > 0 ? (
                <ul className="mb-2 mt-0.5 space-y-0.5 border-l-2 border-slate-200/80 pl-3">
                  {item.children.map((c) => (
                    <li key={`${c.to}::${c.label}`}>
                      <Link
                        to={c.to}
                        className={subItemClass(c.to)}
                        style={itemStyle(c.to)}
                        onClick={(e) => {
                          scrollTopIfSamePage(e, location, c.to)
                          handleNav()
                        }}
                      >
                        {c.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
